import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetRolePermissionsQuery, useUpdateRolePermissionsMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminRolePermissions: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetRolePermissionsQuery(id!);
  const [updateRolePermissions, { isLoading: isUpdating }] = useUpdateRolePermissionsMutation();

  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [expandedTabs, setExpandedTabs] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (data?.data) {
      setSelectedPermissions(data.data.assigned_permissions || []);
    }
  }, [data]);

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleTabToggle = (tab: string) => {
    setExpandedTabs(prev => ({
      ...prev,
      [tab]: !prev[tab],
    }));
  };

  const handleSelectAll = (tab: string, permissions: any[]) => {
    const tabPermissionIds = permissions.map((p: any) => p.id);
    const allSelected = tabPermissionIds.every((id: number) => selectedPermissions.includes(id));
    
    if (allSelected) {
      // Deselect all permissions in this tab
      setSelectedPermissions(prev => prev.filter(id => !tabPermissionIds.includes(id)));
    } else {
      // Select all permissions in this tab
      setSelectedPermissions(prev => {
        const newIds = tabPermissionIds.filter((id: number) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!window.confirm(t('Are you sure you want to update permissions for this role?'))) {
      return;
    }
    
    try {
      const result = await updateRolePermissions({ id: parseInt(id!), permissions: selectedPermissions }).unwrap();
      showSuccess(result.message || 'Role permissions updated successfully!');
      navigate('/admin/roles');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update role permissions. Please try again.');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const role = data?.data?.role;
  const permissionsByTab = data?.data?.permissions_by_tab || {};

  const tabs = Object.keys(permissionsByTab);

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Role Permissions')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/roles'); }} className="text-muted text-hover-primary">{t('Roles')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-10">
                <h2 className="mb-5">{t('Role:')} {role?.name}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="accordion" id="accordionPermissions">
                    {tabs.map((tab) => {
                      const permissions = permissionsByTab[tab] || [];
                      const tabPermissionIds = permissions.map((p: any) => p.id);
                      const allSelected = tabPermissionIds.length > 0 && tabPermissionIds.every((id: number) => selectedPermissions.includes(id));
                      const isExpanded = expandedTabs[tab] || false;

                      return (
                        <div key={tab} className="accordion-item">
                          <h2 className="accordion-header" id={`heading${tab}`}>
                            <button
                              className={`accordion-button ${isExpanded ? '' : 'collapsed'} bg-light fw-bold border`}
                              type="button"
                              onClick={() => handleTabToggle(tab)}
                              aria-expanded={isExpanded}
                              aria-controls={`collapse${tab}`}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input me-3"
                                checked={allSelected}
                                onChange={() => handleSelectAll(tab, permissions)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              {tab.toUpperCase()}
                            </button>
                          </h2>
                          <div
                            id={`collapse${tab}`}
                            className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}
                            aria-labelledby={`heading${tab}`}
                            data-bs-parent="#accordionPermissions"
                          >
                            <div className="accordion-body">
                              {permissions.map((permission: any) => (
                                <div key={permission.id} className="mb-3">
                                  <label className="form-check-label d-flex align-items-center text-white">
                                    <input
                                      type="checkbox"
                                      className="form-check-input me-3 text-white"
                                      checked={selectedPermissions.includes(permission.id)}
                                      onChange={() => handlePermissionToggle(permission.id)}
                                    />
                                    <span>{permission.name}</span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="text-center mt-5">
                    <button type="button" className="btn btn-light me-3" onClick={() => navigate('/admin/roles')}>
                      {t('Cancel')}
                    </button>
                    <button type="submit" className="btn btn-base" disabled={isUpdating}>
                      <span className="indicator-label">{t('Update Permissions')}</span>
                      {isUpdating && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRolePermissions;

